// LICENSE : MIT
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var Dispatcher_1 = require("./Dispatcher");
var DispatcherPayloadMeta_1 = require("./DispatcherPayloadMeta");
var Store_1 = require("./Store");
var StoreGroupValidator_1 = require("./UILayer/StoreGroupValidator");
var UseCaseUnitOfWork_1 = require("./UnitOfWork/UseCaseUnitOfWork");
var StoreGroup_1 = require("./UILayer/StoreGroup");
var UseCaseExecutorFactory_1 = require("./UseCaseExecutorFactory");
var SingleStoreGroup_1 = require("./UILayer/SingleStoreGroup");
var LifeCycleEventHub_1 = require("./LifeCycleEventHub");
var StoreChangedPayload_1 = require("./payload/StoreChangedPayload");
var AlminInstruments_1 = require("./instrument/AlminInstruments");
var ContextConfig_1 = require("./ContextConfig");
var deprecateWarning = function (methodName) {
    console.warn("Deprecated: Context.prototype." + methodName + " is deprecated.\nPlease use Context.prototype.events." + methodName + " instead of it.\nSee https://github.com/almin/almin/releases/tag/almin%400.13.10 for more details.\n");
};
/**
 * Context class provide observing and communicating with **Store** and **UseCase**.
 */
var Context = /** @class */ (function () {
    /**
     * Context should be initialized with `store` that is an instance of StoreLike implementation
     *
     * ### Example
     *
     * It is minimal initialization.
     *
     * ```js
     * const context = new Context({
     *   store: new MyStore()
     * });
     * ```
     *
     * In real case, you can pass `StoreGroup` instead of `Store`.
     *
     * ```js
     * const storeGroup = new StoreGroup([
     *   new AStore(), new BStore(), new CStore()
     * ]);
     * const context = new Context({
     *   store: storeGroup
     * });
     * ```
     */
    function Context(args) {
        var _this = this;
        var store = args.store;
        StoreGroupValidator_1.StoreGroupValidator.validateInstance(store);
        // Central dispatcher
        // Almin 0.16+: dispatcher is optional.
        // https://github.com/almin/almin/issues/185
        this.dispatcher = args.dispatcher || new Dispatcher_1.Dispatcher();
        // Implementation Note:
        // Delegate dispatch event to Store|StoreGroup from Dispatcher
        // StoreGroup call each Store#receivePayload, but pass directly Store is not.
        // So, Context check the store instance has implementation of `Store#receivePayload` and pass payload to it.
        // See https://github.com/almin/almin/issues/190
        // createSingleStoreGroup is wrapper of store for creating StoreGroup.
        if (store instanceof StoreGroup_1.StoreGroup) {
            this.storeGroup = store;
        }
        else if (store instanceof Store_1.Store) {
            this.storeGroup = SingleStoreGroup_1.createSingleStoreGroup(store);
        }
        else {
            throw new Error("{ store } should be instanceof StoreGroup or Store.");
        }
        // life-cycle event hub
        this.lifeCycleEventHub = new LifeCycleEventHub_1.LifeCycleEventHub({
            dispatcher: this.dispatcher
        });
        var options = args.options || {};
        this.config = new ContextConfig_1.ContextConfig({
            strict: options.strict,
            performanceProfile: options.performanceProfile
        });
        if (this.config.strict) {
            this.storeGroup.useStrict();
        }
        // If some stores are changed, emit StoreChangedPayload
        // Store -> StoreGroup -> LifeCycleEventHub
        var storeGroupOnChangeToStoreChangedPayload = function (stores, details) {
            stores.forEach(function (store) {
                var payload = new StoreChangedPayload_1.StoreChangedPayload(store);
                // Should not included in StoreChanged Event
                // inherit some context from the reason of change details
                var transaction = details && details.meta && details.meta.transaction;
                var isUseCaseFinished = details && details.meta && details.meta.isUseCaseFinished;
                var meta = new DispatcherPayloadMeta_1.DispatcherPayloadMetaImpl({
                    // StoreChangedPayload is always trusted.
                    // Because, StoreChangedPayload is created by almin, not user.
                    // Related issue: https://github.com/almin/almin/issues/328
                    isTrusted: true,
                    isUseCaseFinished: isUseCaseFinished,
                    transaction: transaction
                });
                _this.dispatcher.dispatch(payload, meta);
            });
        };
        this.storeGroup.onChange(storeGroupOnChangeToStoreChangedPayload);
        // Instruments Transaction/UseCase lifecycle
        if (process.env.NODE_ENV !== "production" && this.config.performanceProfile && AlminInstruments_1.default.debugTool) {
            var debugTool_1 = AlminInstruments_1.default.debugTool;
            debugTool_1.beginProfile();
            this.lifeCycleEventHub.onBeginTransaction(function (_payload, meta) {
                meta.transaction && debugTool_1.beginTransaction(meta.transaction.id, meta.transaction);
            });
            this.lifeCycleEventHub.onWillExecuteEachUseCase(function (_payload, meta) {
                meta.useCase && debugTool_1.willUseCaseExecute(meta.useCase.id, meta.useCase);
            });
            this.lifeCycleEventHub.onDidExecuteEachUseCase(function (_payload, meta) {
                meta.useCase && debugTool_1.didUseCaseExecute(meta.useCase.id, meta.useCase);
            });
            this.lifeCycleEventHub.onCompleteEachUseCase(function (_payload, meta) {
                meta.useCase && debugTool_1.completeUseCaseExecute(meta.useCase.id, meta.useCase);
            });
            this.lifeCycleEventHub.onEndTransaction(function (_payload, meta) {
                meta.transaction && debugTool_1.endTransaction(meta.transaction.id, meta.transaction);
            });
        }
    }
    Object.defineProperty(Context.prototype, "events", {
        /**
         * Return almin life cycle events hub.
         * You can observe life cycle events on almin.
         *
         * See LifeCycleEventHub
         */
        get: function () {
            return this.lifeCycleEventHub;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Return state value of StoreGroup or Store.
     *
     * ### Example
     *
     * If you pass `StoreGroup` to `store` of Constructor,
     * `Context#getState()` return the state object that merge each stores's state.
     *
     * ```js
     * const state = context.getState();
     * console.log(state);
     * // { aState, bState }
     * ```
     */
    Context.prototype.getState = function () {
        return this.storeGroup.getState();
    };
    /**
     * If anyone store that is passed to constructor is changed, `handler` is called.
     * `onChange` arguments is an array of `Store` instances that are changed.
     *
     * It returns unSubscribe function.
     * If you want to release handler, the returned function.
     *
     * It is useful for updating view in the `handler`.
     *
     * ### Example
     *
     * ```js
     * const unSubscribe = context.onChange(changingStores => {
     *   console.log(changingStores); // Array<Store>
     *   // Update view
     * });
     * ```
     *
     *
     * ## Notes
     *
     * If you want to know the change of registered store, please use `context.onChange`.
     * `context.onChange` is optimized for updating View.
     * By contrast, `context.events.*` is not optimized data. it is useful for logging.
     *
     */
    Context.prototype.onChange = function (handler) {
        return this.storeGroup.onChange(handler);
    };
    Context.prototype.useCase = function (useCase) {
        var useCaseExecutor = UseCaseExecutorFactory_1.createUseCaseExecutor(useCase, this.dispatcher);
        var unitOfWork = new UseCaseUnitOfWork_1.UseCaseUnitOfWork({
            name: "Default",
            dispatcher: this.dispatcher,
            storeGroup: this.storeGroup,
            options: { autoCommit: true }
        });
        unitOfWork.open(useCaseExecutor);
        useCaseExecutor.onReleaseOnce(function () {
            unitOfWork.close(useCaseExecutor);
            unitOfWork.release();
        });
        return useCaseExecutor;
    };
    /**
     * ## Transaction
     *
     * - **Stability**: Experimental
     * - This feature is subject to change. It may change or be removed in future versions.
     *
     * Create new Unit of Work and execute UseCase.
     * You can prevent heavy updating of StoreGroup.
     *
     * This feature only work in strict mode.
     *
     * Difference with `Context#useCase`:
     *
     * - Do not update StoreGroup automatically
     * - You should call `transactionContext.commit()` to update StoreGroup at any time
     *
     * ## Example
     *
     * ```js
     * context.transaction("A->B->C transaction", transactionContext => {
     *      return transactionContext.useCase(new ChangeAUseCase()).execute() // no update store
     *          .then(() => {
     *              return transactionContext.useCase(new ChangeBUseCase()).execute(); // no update store
     *          }).then(() => {
     *              return transactionContext.useCase(new ChangeCUseCase()).execute(); // no update store
     *          }).then(() => {
     *              transactionContext.commit(); // update store
     *              // replay: ChangeAUseCase -> ChangeBUseCase -> ChangeCUseCase
     *          });
     *  });
     * ```
     *
     * ## Notes
     *
     * ### Transaction should be commit or exit
     *
     * Transaction context should be called `commit()` or `exit()`.
     * Because, this check logic avoid to silent failure of transaction.
     *
     * And, transaction context should return a promise.
     * In most case, transaction context should return `transactionContext.useCase(useCase).execute()`.
     *
     * ### Transaction disallow to do multiple commits
     *
     * A transaction can do a single `commit()`
     * Not to allow to do multiple commits in a transaction.
     *
     * Use multiple transaction chain insteadof multiple commit in a transaction.
     *
     * If you want to multiple commit, please file issue with the motivation.
     *
     * ### Transaction is not lock system
     *
     * The **transaction** does not lock updating of stores.
     * The **transaction** method that create a new unit of work.
     *
     * It means that the store may be updated by other unit of work during executing `context.transaction`.
     * `context.transaction` provide the way for bulk updating.
     *
     * A Unit of Work promise the order of events in the Unit of Work.
     * But, This don't promise the order of events between Unit of Works.
     *
     * ```
     *  -----------------  commit   -----------------
     * | Unit of Work A |  ------> |                 |
     *  ----------------           |    StoreGroup   |
     *  -----------------  commit  |                 |
     * | Unit of Work B |  ------> |                 |
     *  ----------------            -----------------
     * ```
     *
     * Current implementation is **READ COMMITTED** of Transaction Isolation Levels.
     *
     * - <https://en.wikipedia.org/wiki/Isolation_(database_systems)>
     *
     * ### No commit transaction get cancelled
     *
     * You can write no `commit()` transaction.
     * This transaction add commitment to the unit of work, but does not `commit()`.
     *
     * ```js
     * context.transaction("No commit transaction", transactionContext => {
     *      // No commit - Call `transactionContext.exit()` insteadof `transactionContext.commit()`
     *      return transactionContext.useCase(new LogUseCase()).execute().then(() => {
     *          transactionContext.exit();
     *      });
     * });
     * ```
     *
     * As a result, This transaction does not affect to Store.
     * It means that Store can't received the payload of `LogUseCase`.
     * Finally, that commitment get cancelled.
     *
     * It is useful for logging UseCase.
     * Logging UseCase does not need to update store/view.
     * It only does log/send data to console/server.
     *
     * ### TODO: rollback is not implemented
     *
     * Rollback feature is generality implemented in the unit of work.
     * We want to know actual use case of rollback before implementing this.
     *
     */
    Context.prototype.transaction = function (name, transactionHandler) {
        var _this = this;
        if (process.env.NODE_ENV !== "production") {
            if (!this.config.strict) {
                console.error("Warning(Context): Context#transaction only use in strict mode.\nBecause, the transaction should have reliability of updating stores. strict mode promise it.\nPlease enable strict mode via `new Context({ dispatcher, store, options: { strict: true });`\n");
            }
        }
        var unitOfWork = new UseCaseUnitOfWork_1.UseCaseUnitOfWork({
            name: name,
            dispatcher: this.dispatcher,
            storeGroup: this.storeGroup,
            options: { autoCommit: false }
        });
        var createUseCaseExecutorAndOpenUoW = function (useCase) {
            var useCaseExecutor = UseCaseExecutorFactory_1.createUseCaseExecutor(useCase, _this.dispatcher);
            unitOfWork.open(useCaseExecutor);
            return useCaseExecutor;
        };
        var context = {
            id: unitOfWork.id,
            useCase: createUseCaseExecutorAndOpenUoW,
            commit: function () {
                unitOfWork.commit();
            },
            exit: function () {
                unitOfWork.exit();
            }
        };
        // Start Transaction
        unitOfWork.beginTransaction();
        // - transactionContext will resolve with void
        // - unitOfWork automatically close when transactionContext is exited
        // It is by design.
        var promise = new Promise(function (resolve, reject) {
            var promise = transactionHandler(context);
            var isResultPromise = typeof promise === "object" && promise !== null && typeof promise.then == "function";
            // if the transaction failed, exit the transaction and throw error
            if (!isResultPromise) {
                unitOfWork.exit();
                return reject(new Error("Error(Transaction): transactionHandler should return promise.\nTransaction should be exited after all useCases have been completed.\n\nFor example, following transaction will be exited after SomeUseCase is completed.\n\ncontext.transaction(\"transaction\", transactionContext => {\n     return transactionContext.useCase(new SomeUseCase()).execute();\n});          \n"));
            }
            resolve(promise);
        });
        return promise.then(function () {
            unitOfWork.release();
        }, function (error) {
            unitOfWork.release();
            return Promise.reject(error);
        });
    };
    /**
     * Register `handler` function to Context.
     * `handler` is called when each useCases will execute.
     * @deprecated
     * Use `context.events.onWillExecuteEachUseCase` insteadof it.
     */
    Context.prototype.onWillExecuteEachUseCase = function (handler) {
        deprecateWarning("onWillExecuteEachUseCase");
        return this.lifeCycleEventHub.onWillExecuteEachUseCase(handler);
    };
    /**
     * Register `handler` function to Context.
     * `handler` is called the `handler` with user-defined payload object when the UseCase dispatch with payload.
     * This `onDispatch` is not called at built-in event. It is filtered by Context.
     * If you want to *All* dispatched event and use listen directly your `dispatcher` object.
     * In other word, listen the dispatcher of `new Context({dispatcher})`.
     *
     * ### Example
     *
     * ```js
     * const dispatchUseCase = ({dispatcher}) => {
     *   return () => dispatcher.dispatch({ type: "fired-payload" });
     * };
     * context.onDispatch((payload, meta) => {
     *   console.log(payload); // { type: "fired-payload" }
     * });
     *
     * context.useCase(dispatchUseCase).execute();
     * ```
     *
     * @deprecated
     * Use `context.events.onDispatch` insteadof it.
     */
    Context.prototype.onDispatch = function (handler) {
        deprecateWarning("onDispatch");
        return this.lifeCycleEventHub.onDispatch(handler);
    };
    /**
     * `handler` is called when each useCases are executed.
     * @deprecated
     * Use `context.events.onDidExecuteEachUseCase` insteadof it.
     */
    Context.prototype.onDidExecuteEachUseCase = function (handler) {
        deprecateWarning("onDidExecuteEachUseCase");
        return this.lifeCycleEventHub.onDidExecuteEachUseCase(handler);
    };
    /**
     * `handler` is called when each useCases are completed.
     * This `handler` is always called asynchronously.
     * @deprecated
     * Use `context.events.onCompleteEachUseCase` insteadof it.
     */
    Context.prototype.onCompleteEachUseCase = function (handler) {
        deprecateWarning("onCompleteEachUseCase");
        return this.lifeCycleEventHub.onCompleteEachUseCase(handler);
    };
    /**
     * `handler` is called when some UseCase throw Error.
     *
     * Throwing Error is following case:
     *
     * - Throw exception in a UseCase
     * - Return rejected promise in a UseCase
     * - Call `UseCase#throwError(error)`
     * @deprecated
     * Use `context.events.onErrorDispatch` insteadof it.
     */
    Context.prototype.onErrorDispatch = function (handler) {
        deprecateWarning("onErrorDispatch");
        return this.lifeCycleEventHub.onErrorDispatch(handler);
    };
    /**
     * Release all events handler in Context.
     * You can call this when no more call event handler
     */
    Context.prototype.release = function () {
        this.storeGroup.release();
        this.lifeCycleEventHub.release();
    };
    return Context;
}());
exports.Context = Context;
//# sourceMappingURL=Context.js.map


//////////////////
// WEBPACK FOOTER
// /home/travis/build/proofdict/proofdict/node_modules/almin/lib/src/Context.js
// module id = 478
// module chunks = 0